import { useEffect, useState, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	Box,
	Grid,
	TextField,
	Typography,
	CircularProgress,
	Autocomplete,
	Button,
	Card,
	CardContent,
	Chip,
} from "@mui/material";
import { obtenerDatos, obtenerDatosPostDash, auditPost } from "../../api/config";
// import { toPng } from "html-to-image";
// import saveAs from "file-saver";
import { GlobalContext } from "../../GlobalContext";
import { LineChartMUI, StackedBarChartMUI, TreemapChartRecharts } from "./ChartsMUI";
// import BarChartIcon from "@mui/icons-material/BarChart";

const getMesesNombres = (t) => [
	{ id: 1, nombre: t("aux.months.Jan") },
	{ id: 2, nombre: t("aux.months.Feb") },
	{ id: 3, nombre: t("aux.months.Mar") },
	{ id: 4, nombre: t("aux.months.Apr") },
	{ id: 5, nombre: t("aux.months.May") },
	{ id: 6, nombre: t("aux.months.Jun") },
	{ id: 7, nombre: t("aux.months.Jul") },
	{ id: 8, nombre: t("aux.months.Aug") },
	{ id: 9, nombre: t("aux.months.Sep") },
	{ id: 10, nombre: t("aux.months.Oct") },
	{ id: 11, nombre: t("aux.months.Nov") },
	{ id: 12, nombre: t("aux.months.Dec") },
];

const Dashboard = () => {
	const { t } = useTranslation();
	const { getModuleId, site_id } = useContext(GlobalContext);
	const [isLoading, setIsLoading] = useState(false);
	const [keySimulacion, setKeySimulacion] = useState([]);
	const [selectedSimulacion, setSelectedSimulacion] = useState([]);
	const [optionsLoading, setOptionsLoading] = useState(false);
	const [dataComplete, setDataComplete] = useState([]);
	// const [isDownloading, setIsDownloading] = useState(false);
	// Renombra este estado
	const [periodos, setPeriodos] = useState([]);
	// en lugar de [mes, setMes]

	// Crea también un estado para los periodos seleccionados
	const [selectedPeriodos, setSelectedPeriodos] = useState([]);

	// Mantén esto, para los nombres de mes:
	const mesesNombres = useMemo(() => getMesesNombres(t), [t]);

	useEffect(() => {
		const fetchSimulacion = async () => {
			setOptionsLoading(true);
			try {
				if (site_id !== null) {
					const datos = await obtenerDatos(`/simulacion/by_site_id/${site_id}`);

					const datosKeys = Object.keys(datos).map((key) => key);
					if (datosKeys && datosKeys.length > 0) {
						const datosNames = Object.keys(datos).map((key) => ({
							name: datos[key].name ?? t("aux.no_name"),
							value: datos[key].simulacion_id,
						}));
						setKeySimulacion(datosNames);
					} else {
						setKeySimulacion([]);
					}
				}
			} catch (error) {
				console.error("Error al obtener datos de la simulación por defecto:", error);
			} finally {
				setOptionsLoading(false);
			}
		};
		fetchSimulacion();
	}, [site_id, t]);

	const handleClicked = async () => {
		try {
			if (selectedSimulacion) {
				setIsLoading(true);
				const dataValues = selectedSimulacion.map((sim) => sim.value);
				const dataFetched = await obtenerDatosPostDash(site_id, dataValues);

				// Auditar la acción
				const moduleId = getModuleId(
					"Dashboard",
					"Ver los resultados de todas las simulaciones del site (get output simulation by site_id)"
				);
				if (moduleId !== null) {
					auditPost(moduleId, site_id);
				}

				setDataComplete(dataFetched);

				// Extraer meses únicos de la data
				const allKeys = new Set();
				dataFetched.forEach((sim) =>
					Object.keys(sim)
						.filter((key) => /^\d{4}-\d{2}$/.test(key))
						.forEach((key) => allKeys.add(key))
				);

				// 2) Mapear a objetos { id, year, month, label }
				const periodosArr = Array.from(allKeys)
					.map((key) => {
						const [yearStr, monthStr] = key.split("-");
						const year = Number(yearStr);
						const month = Number(monthStr);
						const nombre = mesesNombres.find((mn) => mn.id === month)?.nombre || monthStr;
						return { id: key, year, month, label: `${nombre} ${year}` };
					})
					.sort((a, b) => (a.year !== b.year ? a.year - b.year : a.month - b.month));

				setPeriodos(periodosArr);
				// 3) Por defecto puedes seleccionar todos los periodos
				setSelectedPeriodos(periodosArr);
			}
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	// const handleDownloadCharts = async () => {
	// 	setIsDownloading(true);
	// 	try {
	// 		const container = document.getElementById("charts-container");
	// 		const dataUrl = await toPng(container, { quality: 1 });
	// 		const blob = dataURItoBlob(dataUrl);
	// 		saveAs(blob, "charts.png");

	// 		const moduleId = getModuleId("Dashboard", "Descargar gráficos");
	// 		if (moduleId !== null) {
	// 			auditPost(moduleId, site_id);
	// 		}
	// 	} catch (error) {
	// 		console.error("Error al generar la imagen PNG:", error);
	// 	} finally {
	// 		setIsDownloading(false);
	// 	}
	// };

	// const dataURItoBlob = (dataURI) => {
	// 	const byteString = atob(dataURI.split(",")[1]);
	// 	const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
	// 	const ab = new ArrayBuffer(byteString.length);
	// 	const ia = new Uint8Array(ab);
	// 	for (let i = 0; i < byteString.length; i++) {
	// 		ia[i] = byteString.charCodeAt(i);
	// 	}
	// 	return new Blob([ab], { type: mimeString });
	// };

	// Filtrar simulaciones válidas y con error
	const simValidas = dataComplete.filter((sim) => !sim.error);
	const simErrores = dataComplete.filter((sim) => sim.error);

	// Calculamos el ancho de columna en base a la cantidad de simulaciones válidas
	const columns =
		simValidas.length === 3 ? 4 : simValidas.length === 2 ? 6 : simValidas.length === 1 ? 12 : 12;

	return (
		<Box
			sx={{
				width: "100%",
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				flexDirection: "column",
			}}
		>
			<Box sx={{ p: 1, width: "85%", textAlign: "center" }}>
				{/* Título y autocompletes */}
				<Grid container spacing={0.5} alignItems="center">
					<Grid item xs={10}>
						<Autocomplete
							size="small"
							multiple
							options={keySimulacion ?? []}
							loading={optionsLoading}
							value={selectedSimulacion}
							onChange={(event, newValue) => {
								if (newValue.length <= 3) {
									setSelectedSimulacion(newValue);
								}
							}}
							getOptionLabel={(option) => `${option.value} | ${option.name}`}
							limitTags={3}
							isOptionEqualToValue={(opt, val) => opt.value === val.value}
							renderInput={(params) => (
								<TextField
									{...params}
									label={t("aux.messages.select_three_simulations")}
									variant="outlined"
									size="small"
									sx={{
										// Ajusta el tamaño del input para que sea más delgado.
										"& .MuiInputBase-root": {
											padding: "4px",
											height: "36px",
										},
									}}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={2}>
						<Button
							fullWidth
							size="medium"
							variant="contained"
							color="primary"
							onClick={handleClicked}
							disabled={keySimulacion.length === 0}
							sx={{
								height: "36px",
								minHeight: "36px",
							}}
						>
							{t("aux.buttons.compare")}
						</Button>
					</Grid>
				</Grid>

				{/* Mostrar mensaje de error para simulaciones sin data */}
				{simErrores.length > 0 && (
					<Box sx={{ mt: 2 }}>
						{simErrores.map((sim, idx) => (
							<Typography key={idx} variant="body2" color="error" align="center">
								{t("charts.simulation")} {sim.simulacion_id}: {sim.error}
							</Typography>
						))}
					</Box>
				)}

				{/* Loader */}
				{isLoading && (
					<Box
						sx={{
							mt: 4,
							width: "100%",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
						}}
					>
						<CircularProgress color="primary" />
					</Box>
				)}

				{/* Contenido de gráficos */}
				{!isLoading && dataComplete && dataComplete.length > 0 && (
					<>
						{/* Filtro de mes y botón de descarga */}
						<Box sx={{ width: "100%", mx: "auto", my: 2 }}>
							<Grid container spacing={0.5} alignItems="center" justifyContent="flex-start">
								<Grid item xs={12}>
									<Autocomplete
										fullWidth
										size="small"
										multiple
										options={periodos}
										value={selectedPeriodos}
										onChange={(e, v) => setSelectedPeriodos(v)}
										getOptionLabel={(opt) => opt.label}
										isOptionEqualToValue={(opt, val) => opt.id === val.id}
										renderTags={(value, getTagProps) =>
											value.map((option, index) => {
												const tagProps = getTagProps({ index });
												const { key, ...other } = tagProps; // separamos key
												return (
													<Chip
														key={option.id} // aquí va tu propio key
														{...other} // resto de props sin key
														label={option.label}
														sx={{ m: 0.25 }}
													/>
												);
											})
										}
										renderInput={(params) => (
											<TextField
												{...params}
												label={t("aux.filter_per_month")}
												variant="outlined"
												InputProps={{
													...params.InputProps,
													sx: {
														flexWrap: "wrap", // permitir que los chips hagan wrap
														alignItems: "flex-start",
														minHeight: "56px", // sube esto para dos líneas cómodas
													},
												}}
											/>
										)}
										sx={{
											"& .MuiAutocomplete-inputRoot": {
												flexWrap: "wrap",
												alignItems: "flex-start",
												overflow: "hidden",
											},
										}}
									/>
								</Grid>
								{/* <Grid item xs={4}>
									<Button
										fullWidth
										variant="outlined"
										color="primary"
										disabled={isDownloading || !isGranted("Dashboard", "Descargar gráficos")}
										onClick={handleDownloadCharts}
										startIcon={<BarChartIcon />}
									>
										{isDownloading ? t("aux.downloading") : t("aux.buttons.chart.download_chart")}
									</Button>
								</Grid> */}
							</Grid>
						</Box>

						{/* Contenedor general de gráficos para simulaciones válidas */}
						{simValidas.length > 0 && (
							<Box sx={{ mt: 1 }} id="charts-container">
								<Grid container spacing={1}>
									{simValidas.map((sim, index) => (
										<Grid item xs={columns} key={index}>
											<Box sx={{ mb: 2 }}>
												<Typography variant="h6">
													{t("charts.simulation")} {sim.simulacion_id}
												</Typography>
												{/* Todos los gráficos agrupados en una sola Card */}
												<Card sx={{ mb: 1 }}>
													<CardContent>
														<Grid container spacing={1}>
															{/* Gráfico de líneas */}
															<Grid item xs={12}>
																{sim.error ? (
																	<Typography variant="subtitle1">{t("aux.no_data")}</Typography>
																) : (
																	<LineChartMUI data={sim} selectedPeriods={selectedPeriodos} />
																)}
															</Grid>

															{/* Gráfico de barras apiladas */}
															<Grid item xs={12}>
																{sim.error ? (
																	<Typography variant="subtitle1">{t("charts.no_data")}</Typography>
																) : (
																	<StackedBarChartMUI data={sim} selectedPeriods={selectedPeriodos} />
																)}
															</Grid>
															{/* Treemap */}
															<Grid item xs={12}>
																{sim.error ? (
																	<Typography variant="subtitle1">{t("charts.no_data")}</Typography>
																) : (
																	<TreemapChartRecharts data={sim} selectedPeriods={selectedPeriodos} />
																)}
															</Grid>
														</Grid>
													</CardContent>
												</Card>
											</Box>
										</Grid>
									))}
								</Grid>
							</Box>
						)}
					</>
				)}

				{/* Mensaje si no hay data */}
				{!isLoading && (!dataComplete || dataComplete.length === 0) && (
					<Box sx={{ mt: 2 }}>
						<Typography variant="subtitle1">{t("charts.loading_data")}</Typography>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export { Dashboard };
